import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { combineApartmentData } from '@/modules/Housing/modules/ApartmentSetup/lib';
import { useStableCallback } from '@/hooks';
import { formatCurrencyStringToNumber, formatNumberToCurrencyString } from '@/lib/utils';
import { useDispatch } from 'react-redux';
import {
  createApartmentAsync,
  updateApartmentAsync,
  setSelectedApartmentAction,
  uploadDocumentAsync,
} from '@/modules/Housing/redux/apartment';
import { apartmentConstants } from '@/modules/Housing/lib';

const {
  DOCUMENT_UPLOAD_FORM_NAME,
} = apartmentConstants;

export function useFormActions({
  validationSchema,
  formData,
  nextStep,
  complexId,
  apartmentId,
  selected,
  formDataName,
}) {
  const dispatch = useDispatch();
  const methods = useForm({
    defaultValues: formData,
    mode: 'all',
    reValidateMode: 'onChange',
    ...(validationSchema && { resolver: yupResolver(validationSchema) }),
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    reset,
  } = methods;

  useEffect(() => {
    reset({
      ...formData,
    });
  }, [formData, reset]);

  const handleChange = useCallback((event) => {
    const { name, type, files } = event.target;
    let { value } = event.target;

    value = type === 'file' ? files[0] : value;

    setValue(name, value, { shouldValidate: true, shouldDirty: true, shouldTouch: type !== 'file' });
  }, [
    setValue,
  ]);

  const onChangeDecimalCurrency = (event, name) => {
    handleChange({
      target: {
        name,
        value: formatNumberToCurrencyString(
          formatCurrencyStringToNumber(event.target.value),
          2,
        ),
      },
      type: event.type,
    });
  };

  const handleSubmitForm = useStableCallback((onSuccess) => {
    let action;
    let successCallback;

    const sendingData = {
      ...selected,
      [formDataName] : getValues(),
    };

    const preparedData = Object.entries(sendingData).reduce((acc, [, data]) => {
      return { ...acc, ...combineApartmentData(data || {}) };
    }, {});

    if (apartmentId) {
      if (formDataName === DOCUMENT_UPLOAD_FORM_NAME) {
        action = uploadDocumentAsync.request;
      } else {
        action = updateApartmentAsync.request;
      }

      successCallback = onSuccess ?? (() => {});
    } else {
      action = createApartmentAsync.request;
      successCallback = (apartmentId) => {
        dispatch(uploadDocumentAsync.request({
          data: preparedData,
          complexId,
          apartmentId,
          successCallback: onSuccess,
        }));
      };
    }

    dispatch(action({
      data: preparedData,
      complexId,
      ...(apartmentId && { apartmentId: apartmentId }),
      successCallback,
    }));
  });

  useEffect(() => {
    if (nextStep) {
      const values = getValues();

      if (selected.is_archived) {
        nextStep.applyTransition();
      } else {
        handleSubmit(() => {
          dispatch(setSelectedApartmentAction({
            [formDataName]: values,
          }));
          nextStep.applyTransition();
        })();
      }
    }
  }, [
    selected.is_archived,
    nextStep,
    handleSubmit,
    handleSubmitForm,
    apartmentId,
    formDataName,
    getValues,
    dispatch,
  ]);

  return {
    methods,
    handleChange,
    handleSubmitForm,
    onChangeDecimalCurrency,
  };
}
